import { X, Star } from "lucide-react";

const DIMENSIONS = [
  { key: "actionability",    label: "Actionability",     desc: "Findings say what to do and why" },
  { key: "specificity",      label: "Specificity",       desc: "Exact names, campaigns, dollar amounts" },
  { key: "coverage",         label: "Coverage",          desc: "Waste, growth, structure, history" },
  { key: "data_utilization", label: "Data Utilization",  desc: "Makes use of all available signals" },
];

function ScoreBar({ value }) {
  const pct = (value / 10) * 100;
  const color = value >= 8 ? "bg-emerald-500" : value >= 6 ? "bg-amber-400" : "bg-rose-500";
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="num w-6 text-right text-xs font-semibold text-slate-700">{value}</span>
    </div>
  );
}

export default function RatingPanel({ rating, onClose }) {
  const score = rating?.score ?? 0;
  const breakdown = rating?.breakdown ?? {};
  const color = score >= 8 ? "text-emerald-600" : score >= 6 ? "text-amber-500" : "text-rose-500";
  const ring  = score >= 8 ? "border-emerald-200 bg-emerald-50" : score >= 6 ? "border-amber-200 bg-amber-50" : "border-rose-200 bg-rose-50";

  return (
    <div className="card-soft mt-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl border-2 ${ring}`}>
            <span className={`num text-2xl font-bold ${color}`}>{score.toFixed(1)}</span>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <Star size={13} strokeWidth={1.8} className={color} fill="currentColor" />
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Analysis rating</p>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-slate-700">{rating?.verdict}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
        >
          <X size={16} strokeWidth={1.8} />
        </button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {DIMENSIONS.map((d) => (
          <div key={d.key} className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold text-slate-700">{d.label}</p>
            </div>
            <ScoreBar value={breakdown[d.key] ?? 0} />
            <p className="mt-1.5 text-[11px] text-slate-400">{d.desc}</p>
          </div>
        ))}
      </div>

      {(rating?.strengths?.length > 0 || rating?.gaps?.length > 0) && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {rating?.strengths?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-600 mb-2">Strengths</p>
              <ul className="space-y-1.5">
                {rating.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {rating?.gaps?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-600 mb-2">Gaps</p>
              <ul className="space-y-1.5">
                {rating.gaps.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
